<div class="page-title">
  <span>
    <span [translate]="'CHEMICALS'"></span>
    <i class="pi pi-angle-right sds-breadcrumb-sep" aria-hidden="true"></i>
    <span>{{ record?.ProductName }}</span>
  </span>
  <span class="page-actions">
    <button class="btn btn-outline-secondary" (click)="goBack()">
      <i class="pi pi-arrow-left"></i>
      <span [translate]="'BACK_LIST'"></span>
    </button>
    <button
      *ngIf="!editMode && !readOnly"
      class="btn btn-outline-primary"
      (click)="enableEdit()"
    >
      <i class="pi pi-pencil"></i>
      <span [translate]="'SDS_EDIT'"></span>
    </button>
    <button
      *ngIf="editMode"
      class="btn btn-success"
      (click)="saveEdit()"
    >
      <i class="pi pi-check"></i>
      <span [translate]="'SAVE'"></span>
    </button>
    <button
      *ngIf="editMode"
      class="btn btn-outline-secondary"
      (click)="cancelEdit()"
    >
      <i class="pi pi-times"></i>
      <span [translate]="'CANCEL'"></span>
    </button>
  </span>
</div>

<div *ngIf="!record" class="sds-detail-empty">
  <i class="pi pi-inbox"></i>
  <p [translate]="'NO_RECORDS_FOUND'"></p>
</div>

<div *ngIf="record" class="sds-detail-body">
  <form [formGroup]="editForm">
    <div class="row">
      <!-- LEFT COLUMN: Product Info + Document -->
      <div class="col-sm-6">
        <!-- Product Information -->
        <div class="sub-panel">
          <div class="sub-panel-title">
            <span [translate]="'SDS_PRODUCT_INFORMATION'"></span>
          </div>
          <div class="sub-panel-content">
            <!-- Product Name -->
            <div class="sds-detail-row">
              <span class="sds-detail-label" [translate]="'SDS_PRODUCT_NAME'"></span>
              <span *ngIf="!editMode" class="sds-detail-value">{{ record.ProductName }}</span>
              <input
                *ngIf="editMode"
                type="text"
                pInputText
                formControlName="ProductName"
                class="ex-full-width"
                maxlength="255"
              />
            </div>

            <!-- Manufacturer -->
            <div class="sds-detail-row">
              <span class="sds-detail-label" [translate]="'SDS_SUPPLIER'"></span>
              <span *ngIf="!editMode" class="sds-detail-value">{{ record.Supplier }}</span>
              <div *ngIf="editMode" class="input-btn-group">
                <p-select
                  [options]="suppliers"
                  formControlName="Supplier"
                  [style]="{ width: '100%' }"
                  [filter]="true"
                  [filterPlaceholder]="'SDS_SEARCH_MANUFACTURERS_PLACEHOLDER' | translate"
                  panelStyleClass="sds-manufacturer-panel"
                >
                  <ng-template let-option pTemplate="item">
                    <div class="sds-manufacturer-row">
                      <span class="sds-manufacturer-name">{{ option.label }}</span>
                      <span *ngIf="option.isDefault" class="sds-manufacturer-badge" [translate]="'SDS_MANUFACTURER_DEFAULT'"></span>
                    </div>
                  </ng-template>
                </p-select>
                <button
                  class="btn btn-outline-primary"
                  type="button"
                  (click)="openAddSupplier()"
                  [pTooltip]="'SDS_ADD_MANUFACTURER_TOOLTIP' | translate"
                  tooltipPosition="top"
                >
                  <i class="pi pi-plus" aria-hidden="true"></i>
                </button>
              </div>
            </div>

            <!-- CAS Number(s) — a document may list several -->
            <div class="sds-detail-row">
              <span class="sds-detail-label">
                <span [translate]="'SDS_CAS_NUMBER'"></span>
                <span *ngIf="editMode && !editForm.controls['NoCasNumber'].value" class="text-danger">*</span>
              </span>
              <span *ngIf="!editMode" class="sds-detail-value">{{ record.NoCasNumber ? ('SDS_NO_CAS_NUMBER' | translate) : (record.CASNumber || '—') }}</span>
              <p-autoComplete [multiple]="true" [typeahead]="false"
                *ngIf="editMode"
                formControlName="CASNumbers"
                separator=","
                [unique]="true"
                [addOnTab]="true"
                [addOnBlur]="true"
                styleClass="ex-full-width sds-cas-chips"
                placeholder="e.g. 67-64-1, 7732-18-5"
              ></p-autoComplete>
              <control-messages
                *ngIf="editMode"
                [control]="editForm.controls['CASNumbers']"
              ></control-messages>

              <label *ngIf="editMode" class="sds-no-cas-option">
                <input type="checkbox" formControlName="NoCasNumber" />
                <span [translate]="'SDS_NO_CAS_NUMBER'"></span>
              </label>

              <!-- Re-scan the saved PDF for CAS numbers (edit mode) -->
              <div *ngIf="editMode" class="sds-cas-detect">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  [disabled]="scanningCasFromPdf || editForm.controls['NoCasNumber'].value"
                  (click)="detectCasFromSavedPdf()"
                >
                  <i class="pi" [ngClass]="scanningCasFromPdf ? 'pi-spin pi-spinner' : 'pi-search'"></i>
                  <span [translate]="'SDS_CAS_DETECT_BTN'"></span>
                </button>
              </div>
            </div>

            <!-- Risk Figure — read-only badge in view mode, editable level in edit mode -->
            <div class="sds-detail-row">
              <span class="sds-detail-label" [translate]="'RISK_FIGURE'"></span>
              <span
                *ngIf="!editMode"
                class="badge"
                [style.background-color]="record.RiskFigure.BackColor"
              >
                {{ record.RiskFigure.ValueText }} ({{ record.RiskFigure.Value }})
              </span>
              <div *ngIf="editMode" class="sds-risk-matrix-edit">
                <div class="sds-risk-matrix-field">
                  <label class="sds-risk-matrix-label" [translate]="'PROBABILITY_FIGURE'"></label>
                  <p-select
                    [options]="figureValue"
                    formControlName="Probability"
                    appendTo="body"
                  ></p-select>
                </div>
                <div class="sds-risk-matrix-field">
                  <label class="sds-risk-matrix-label" [translate]="'CONSEQUENCE_FIGURE'"></label>
                  <p-select
                    [options]="figureValue"
                    formControlName="Consequence"
                    appendTo="body"
                  ></p-select>
                </div>
                <div class="sds-risk-matrix-field">
                  <label class="sds-risk-matrix-label" [translate]="'RISK_FIGURE'"></label>
                  <span class="badge sds-risk-matrix-badge" [style.background-color]="editRiskFigure.color">
                    {{ editRiskFigure.labelKey | translate }} ({{ editRiskFigure.value }})
                  </span>
                </div>
              </div>
            </div>

            <!-- Edited By / Created -->
            <div class="sds-detail-row">
              <span class="sds-detail-label" [translate]="'SDS_CREATED'"></span>
              <span class="sds-detail-value">
                {{ record.CreatedOn | date: 'dd/MM/yyyy' }} by {{ record.EditedBy }}
              </span>
            </div>
          </div>
        </div>

        <!-- Replacement & history (visible in both view and edit mode) -->
        <div class="sub-panel">
          <div class="sub-panel-title">
            <span [translate]="'SDS_REPLACEMENT_TITLE'"></span>
          </div>
          <div class="sub-panel-content">
            <!-- Current replacement state -->
            <div class="sds-detail-row" *ngIf="replacementInfo?.CurrentReplacement as cur">
              <span class="sds-detail-label" [translate]="'SDS_REPLACEMENT_REPLACED_BY'"></span>
              <span class="sds-detail-value">
                <strong>{{ cur.ReplacementProduct }}</strong>
                · {{ cur.ReplacedOn | date: 'dd/MM/yyyy' }}
                <span *ngIf="cur.ReplacedByName">· {{ cur.ReplacedByName }}</span>
              </span>
            </div>

            <div class="sds-detail-row" *ngIf="replacementInfo?.ReplacesActive as prev">
              <span class="sds-detail-label" [translate]="'SDS_REPLACEMENT_REPLACES'"></span>
              <span class="sds-detail-value">{{ prev.OriginalProduct }}</span>
            </div>

            <!-- Actions (only when this chemical can still be replaced, and not mid-edit) -->
            <div class="sds-detail-row" *ngIf="replacementInfo?.CanReplace && !editMode">
              <span class="sds-detail-label" [translate]="'SDS_REPLACEMENT_ACTIONS'"></span>
              <span class="sds-detail-value sds-replacement-actions">
                <button class="btn btn-outline-primary btn-sm" type="button" (click)="openReplaceExisting()">
                  <i class="pi pi-search"></i>
                  <span [translate]="'SDS_REPLACEMENT_USE_EXISTING'"></span>
                </button>
                <button class="btn btn-outline-primary btn-sm" type="button" (click)="replaceWithNew()">
                  <i class="pi pi-plus"></i>
                  <span [translate]="'SDS_REPLACEMENT_CREATE_NEW'"></span>
                </button>
              </span>
            </div>

            <!-- History chain -->
            <div class="sds-replacement-history" *ngIf="replacementInfo?.History?.length">
              <div class="sds-replacement-history-title" [translate]="'SDS_REPLACEMENT_HISTORY'"></div>
              <div class="sds-replacement-chain">
                <div
                  class="sds-replacement-link"
                  *ngFor="let link of replacementInfo!.History"
                  [class.is-current]="isCurrentReplacementLink(link)"
                >
                  <div class="sds-replacement-link-main">
                    <span class="sds-replacement-from">{{ link.OriginalProduct }}</span>
                    <i class="pi pi-arrow-right"></i>
                    <span class="sds-replacement-to">{{ link.ReplacementProduct }}</span>
                    <span
                      class="sds-replacement-status"
                      [ngClass]="isCurrentReplacementLink(link) ? 'is-current' : 'is-historical'"
                    >
                      {{ (isCurrentReplacementLink(link) ? 'SDS_REPLACEMENT_STATUS_CURRENT' : 'SDS_REPLACEMENT_STATUS_HISTORICAL') | translate }}
                    </span>
                  </div>
                  <div class="sds-replacement-link-meta">
                    {{ link.ReplacedOn | date: 'dd/MM/yyyy' }}
                    <span *ngIf="link.ReplacedByName">· {{ link.ReplacedByName }}</span>
                    <span *ngIf="link.Reason" class="sds-replacement-reason">· {{ link.Reason }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="sds-detail-row" *ngIf="!replacementLoading && !replacementInfo?.CurrentReplacement && !replacementInfo?.History?.length && !replacementInfo?.CanReplace">
              <span class="sds-detail-value sds-register-nin-empty">—</span>
            </div>
          </div>
        </div>

        <!-- SDS Document -->
        <div class="sub-panel">
          <div class="sub-panel-title">
            <span [translate]="'SDS_DOCUMENT'"></span>
          </div>
          <div class="sub-panel-content">
            <div class="sds-detail-row" *ngIf="record.FileVersions.length > 0">
              <span class="sds-detail-label" [translate]="'SDS_FILE_VERSION'"></span>
              <p-select
                [options]="fileVersionOptions"
                [ngModel]="record.SelectedFileVersionId"
                [ngModelOptions]="{ standalone: true }"
                [style]="{ width: '100%' }"
                (onChange)="onFileVersionChange($event.value)"
              ></p-select>
            </div>

            <div class="sds-detail-row">
              <span class="sds-detail-label" [translate]="'SDS_UPLOAD_TITLE'"></span>
              <div class="sds-document-upload-actions">
                <button
                  class="btn btn-outline-primary btn-sm"
                  type="button"
                  [disabled]="isUploadingFileVersion"
                  (click)="fileVersionUploadInput.click()"
                >
                  <i class="pi pi-upload"></i>
                  <span>{{
                    isUploadingFileVersion
                      ? ('SDS_UPLOADING_VERSION' | translate)
                      : (hasUploadedPdf ? ('SDS_UPLOAD_NEW_VERSION' | translate) : ('SDS_UPLOAD_PDF' | translate))
                  }}</span>
                </button>
                <button
                  class="btn btn-outline-primary btn-sm"
                  type="button"
                  [disabled]="isUploadingFileVersion"
                  (click)="openArchivePicker()"
                >
                  <i class="pi pi-folder-open"></i>
                  <span [translate]="'SDS_ADD_FROM_ARCHIVE'"></span>
                </button>
                <input
                  #fileVersionUploadInput
                  type="file"
                  accept="application/pdf"
                  hidden
                  (change)="onFileUploadSelected($event)"
                />
              </div>
            </div>

            <app-sds-archive-picker
              [businessId]="businessId"
              [(visible)]="archivePickerVisible"
              (fileSelected)="onArchiveFileSelected($event)"
            ></app-sds-archive-picker>

            <div class="sds-file-card" *ngIf="hasUploadedPdf">
              <div class="sds-file-icon">
                <i class="pi pi-file-pdf"></i>
              </div>
              <div class="sds-file-details">
                <span class="sds-file-name">{{ record.SdsFileName }}</span>
                <span class="sds-file-meta">
                  {{ 'SDS_UPLOADED_LABEL' | translate }} {{ (record.SdsFileUploadedOn || record.CreatedOn) | date: 'dd/MM/yyyy' }}
                </span>
              </div>
              <button
                class="btn btn-outline-primary btn-sm"
                (click)="downloadPdf()"
                [pTooltip]="'SDS_DOWNLOAD_PDF' | translate"
                tooltipPosition="top"
              >
                <i class="pi pi-download"></i>
              </button>
            </div>

            <!-- PDF Preview Card (clickable when PDF available) -->
            <div
              class="sds-pdf-preview-card"
              [class.clickable]="hasPdf"
              (click)="hasPdf ? openPdfPreview() : null"
            >
              <i class="pi pi-file-pdf sds-pdf-preview-card-icon"></i>
              <p *ngIf="hasPdf" class="sds-pdf-preview-card-text" [translate]="'SDS_CLICK_TO_PREVIEW'"></p>
              <p *ngIf="!hasPdf" class="sds-pdf-preview-card-text" [translate]="'SDS_NO_PDF_UPLOADED'"></p>
              <span class="text-muted" *ngIf="hasPdf">{{ record.SdsFileName }}</span>
              <button
                *ngIf="hasPdf"
                class="btn btn-sm sds-preview-open-btn"
                type="button"
              >
                <i class="pi pi-external-link"></i>
                <span [translate]="'SDS_PREVIEW_PDF'"></span>
              </button>
            </div>

            <div class="sds-detail-qr-section">
              <div class="sds-qr-toolbar">
                <button
                  class="btn btn-outline-primary btn-sm"
                  type="button"
                  (click)="generateQrForRecord()"
                  [disabled]="isGeneratingShareLink"
                >
                  <i class="pi pi-qrcode"></i>
                  <span>
                    {{
                      isGeneratingShareLink
                        ? ('SDS_GENERATING' | translate)
                        : (record.QrShareUrl ? ('SDS_REGENERATE_QR' | translate) : ('SDS_GENERATE_QR' | translate))
                    }}
                  </span>
                </button>
                <button
                  class="btn btn-outline-primary btn-sm"
                  type="button"
                  (click)="openShareUrl()"
                  [disabled]="!record.QrShareUrl"
                >
                  <i class="pi pi-external-link"></i>
                  <span [translate]="'SDS_OPEN_LINK'"></span>
                </button>
                <button
                  class="btn btn-outline-secondary btn-sm"
                  type="button"
                  (click)="copyShareUrl()"
                  [disabled]="!record.QrShareUrl"
                >
                  <i class="pi pi-copy"></i>
                  <span [translate]="'SDS_COPY_LINK'"></span>
                </button>
              </div>

                <div class="sds-qr-preview-panel" *ngIf="qrCodeImageUrl">
                  <div class="sds-qr-image-wrap">
                    <img [src]="qrCodeImageUrl" alt="SDS QR Code" class="sds-qr-image" />
                    <div class="sds-qr-logo-wrap">
                      <img src="assets/images/KubaLogo.png" alt="Kuba logo" class="sds-qr-center-logo" />
                    </div>
                  </div>
                </div>

              <button
                class="btn btn-primary btn-sm"
                type="button"
                (click)="printQrA4()"
                [disabled]="isGeneratingShareLink"
              >
                <i class="pi pi-print"></i>
                <span [translate]="'SDS_PRINT_QR_A4'"></span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT COLUMN: Hazard + Controls -->
      <div class="col-sm-6">
        <!-- Hazard Information -->
        <div class="sub-panel">
          <div class="sub-panel-title">
            <span [translate]="'SDS_HAZARD_INFORMATION'"></span>
          </div>
          <div class="sub-panel-content">
            <div class="sds-detail-row">
              <span class="sds-detail-label" [translate]="'SDS_H_STATEMENT_CODE'"></span>
              <span *ngIf="!editMode" class="sds-detail-value sds-h-statement-chips">
                <span
                  *ngFor="let statement of record.HazardStatements"
                  class="sds-h-code-badge"
                  [title]="formatHazardStatement(statement)">
                  {{ statement.Code }}
                </span>
                <span *ngIf="!record.HazardStatements?.length" class="sds-detail-value-empty">—</span>
              </span>
              <sds-hazard-picker *ngIf="editMode" [control]="editForm.controls['HazardStatements']"></sds-hazard-picker>
            </div>

            <div class="sds-detail-row" *ngIf="record.Description || editMode">
              <span class="sds-detail-label" [translate]="'DESCRIPTION'"></span>
              <span *ngIf="!editMode" class="sds-detail-value">{{ record.Description }}</span>
              <textarea
                *ngIf="editMode"
                pTextarea
                formControlName="Description"
                class="ex-description"
                rows="3"
                maxlength="2000"
              ></textarea>
            </div>

            <div class="sds-detail-row" *ngIf="record.KeyRisk || editMode">
              <span class="sds-detail-label" [translate]="'SDS_KEY_RISK'"></span>
              <span *ngIf="!editMode" class="sds-detail-value">{{ record.KeyRisk }}</span>
              <textarea
                *ngIf="editMode"
                pTextarea
                formControlName="KeyRisk"
                class="ex-description"
                rows="2"
                maxlength="2000"
              ></textarea>
            </div>
          </div>
        </div>

        <!-- Use of Product -->
        <div class="sub-panel" *ngIf="record.TaskActivity || editMode">
          <div class="sub-panel-title">
            <span [translate]="'SDS_USE_OF_PRODUCT'"></span>
          </div>
          <div class="sub-panel-content">
            <div class="sds-detail-row" *ngIf="record.TaskActivity || editMode">
              <span class="sds-detail-label" [translate]="'SDS_AREA_OF_USE'"></span>
              <span *ngIf="!editMode" class="sds-detail-value">{{ record.TaskActivity }}</span>
              <textarea
                *ngIf="editMode"
                pTextarea
                formControlName="TaskActivity"
                class="ex-description"
                rows="3"
                maxlength="2000"
              ></textarea>
            </div>

            <div class="sds-detail-row" *ngIf="record.RequiresExposureTracking || editMode">
              <span class="sds-detail-label" [translate]="'SDS_REQUIRES_EXPOSURE_TRACKING'"></span>
              <span *ngIf="!editMode" class="sds-detail-value">
                {{ record.RequiresExposureTracking ? ('YES' | translate) : ('NO' | translate) }}
              </span>
              <p-checkbox
                *ngIf="editMode"
                [binary]="true"
                formControlName="RequiresExposureTracking"
                inputId="detailRequiresExposureTracking"
              ></p-checkbox>
            </div>

            <div class="detail-row" *ngIf="record.RequiresExposureTracking || editForm.value.RequiresExposureTracking">
              <span class="detail-label">
                <span [translate]="'SDS_HAZARD_CATEGORY'"></span>
              </span>
              <span *ngIf="!editMode" class="detail-value">
                {{ hazardCategoryOptions[record?.HazardCategory ?? 0]?.label || ('SDS_HAZARD_CATEGORY_NONE' | translate) }}
              </span>
              <p-select
                *ngIf="editMode"
                formControlName="HazardCategory"
                [options]="hazardCategoryOptions"
                appendTo="body">
              </p-select>
            </div>
          </div>
        </div>

        <!-- Pictograms -->
        <div class="sub-panel">
          <div class="sub-panel-title">
            <span [translate]="'SDS_GHS_PICTOGRAMS'"></span>
          </div>
          <div class="sub-panel-content">
            <div *ngIf="!editMode" class="sds-pictogram-display">
              <span
                *ngFor="let p of record.Pictograms"
                class="sds-pictogram-chip-detail"
                [pTooltip]="getPictogramInfo(p).ghs + ' · ' + (('SDS_PICTOGRAM_' + getPictogramInfo(p).ghs) | translate)"
                tooltipPosition="top"
              >
                <img
                  [src]="getPictogramInfo(p).svg"
                  [alt]="('SDS_PICTOGRAM_' + getPictogramInfo(p).ghs) | translate"
                  class="ghs-pictogram-img-detail"
                />
                <span class="ghs-chip-label">{{ ('SDS_PICTOGRAM_' + getPictogramInfo(p).ghs) | translate }}</span>
              </span>
            </div>
            <div *ngIf="editMode">
              <p class="sds-pictogram-hint" [translate]="'SDS_PICTOGRAM_HINT_NO_LIMIT'"></p>
              <div class="sds-pictogram-grid-detail">
                <div
                  *ngFor="let p of pictogramOptions"
                  class="sds-pictogram-card-sm"
                  [class.selected]="isPictogramSelected(p.id)"
                  (click)="togglePictogram(p.id)"
                  [pTooltip]="p.ghs + ' · ' + (('SDS_PICTOGRAM_' + p.ghs) | translate)"
                  tooltipPosition="top"
                >
                  <img
                    *ngIf="p.svg"
                    [src]="p.svg"
                    [alt]="('SDS_PICTOGRAM_' + p.ghs) | translate"
                    class="sds-pictogram-card-img-sm"
                  />
                  <span class="sds-pictogram-card-label-sm">{{ ('SDS_PICTOGRAM_' + p.ghs) | translate }}</span>
                  <div class="sds-pictogram-check-sm" *ngIf="isPictogramSelected(p.id)">
                    <i class="pi pi-check"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Controls Info (read-only summary) -->
        <div class="sub-panel" *ngIf="record.OtherControls || editMode">
          <div class="sub-panel-title">
            <span [translate]="'SDS_CONTROLS'"></span>
          </div>
          <div class="sub-panel-content">
            <div class="sds-detail-row" *ngIf="record.OtherControls || editMode">
              <span class="sds-detail-label" [translate]="'SDS_OTHER_CONTROLS'"></span>
              <span *ngIf="!editMode" class="sds-detail-value">{{ record.OtherControls || '—' }}</span>
              <textarea
                *ngIf="editMode"
                pTextarea
                formControlName="OtherControls"
                class="ex-description"
                rows="2"
                maxlength="2000"
              ></textarea>
            </div>
          </div>
        </div>

        <div class="sub-panel" *ngIf="record.RequiresExposureTracking || editRequiresExposureTracking">
          <div class="sub-panel-title">
            <span [translate]="'SDS_STEP_EXPOSURE_LOG'"></span>
            <button
              *ngIf="editMode && canAccessEmployeeExposurePii"
              class="btn btn-outline-primary btn-sm"
              type="button"
              (click)="addEmployeeExposure()"
            >
              <i class="pi pi-plus"></i>
              <span [translate]="'SDS_ADD_EXPOSURE_EMPLOYEE'"></span>
            </button>
          </div>
          <div class="sub-panel-content">
            <div class="sds-exposure-warning">
              <i class="pi pi-info-circle"></i>
              <span [translate]="'SDS_EXPOSURE_LOG_WARNING'"></span>
            </div>

            <div *ngIf="!canAccessEmployeeExposurePii" class="sds-detail-value" [translate]="'SDS_EXPOSURE_PII_HIDDEN'"></div>

            <div *ngIf="canAccessEmployeeExposurePii && !editMode" class="sds-exposure-readonly">
              <div class="sds-exposure-readonly-row" *ngFor="let exposure of record.EmployeeExposures || []">
                <strong>{{ exposure.FirstName }} {{ exposure.LastName }}</strong>
                <span>{{ exposure.Email }}</span>
                <span>{{ exposure.Address }}</span>
                <ng-container *ngIf="getRevealedNationalIdentityNumber(exposure) as revealedNin; else maskedNin">
                  <span class="sds-nin-revealed">
                    {{ 'SDS_NIN_PLACEHOLDER' | translate }}: {{ revealedNin }}
                  </span>
                </ng-container>
                <ng-template #maskedNin>
                  <span *ngIf="exposure.NationalIdentityNumberMasked" class="sds-nin-masked">
                    {{ exposure.NationalIdentityNumberMasked }}
                    <button
                      class="btn btn-link btn-sm"
                      type="button"
                      (click)="revealExposureNin(exposure.Id)"
                      [pTooltip]="'SDS_REVEAL_NIN_TOOLTIP' | translate"
                      tooltipPosition="top">
                      <i class="pi pi-eye"></i> {{ 'SDS_REVEAL_NIN' | translate }}
                    </button>
                  </span>
                </ng-template>
                <span *ngIf="exposure.MedicalExamDate">
                  {{ 'SDS_MEDICAL_EXAM_DATE' | translate }}: {{ exposure.MedicalExamDate | date: 'dd/MM/yyyy' }}
                  <ng-container *ngIf="exposure.MedicalExamDoctorName">
                    · {{ exposure.MedicalExamDoctorName }}
                  </ng-container>
                </span>
                <span>
                  {{ exposure.ExposureStartDate | date: 'dd/MM/yyyy' }} - {{ exposure.ExposureEndDate | date: 'dd/MM/yyyy' }}
                </span>
                <span *ngIf="exposure.RetentionExpiryDate" class="sds-retention-hint">
                  {{ 'SDS_RETENTION_RETAIN_UNTIL' | translate }} {{ exposure.RetentionExpiryDate | date: 'yyyy-MM-dd' }}
                </span>
              </div>
            </div>

            <div *ngIf="canAccessEmployeeExposurePii && editMode" formArrayName="EmployeeExposures" class="sds-exposure-grid">
              <div class="sds-exposure-required-legend">
                <span class="sds-required-marker" aria-hidden="true">*</span>
                <span [translate]="'SDS_REQUIRED_FIELD_LEGEND'"></span>
              </div>
              <div
                class="sds-exposure-row"
                *ngFor="let exposure of editEmployeeExposures.controls; let i = index"
                [formGroupName]="i"
              >
                <!-- Pick an existing employee to auto-fill name/email (ID number stays manual). -->
                <div class="sds-field sds-employee-picker" *ngIf="employeeOptions.length">
                  <label class="sds-field-label">
                    <span [translate]="'SDS_EMPLOYEE_PICKER_LABEL'"></span>
                    <small class="sds-field-optional" [translate]="'OPTIONAL'"></small>
                  </label>
                  <p-select
                    [options]="employeeOptions"
                    formControlName="EmployeeUserId"
                    [filter]="true"
                    [showClear]="true"
                    filterBy="label"
                    appendTo="body"
                    styleClass="sds-employee-picker-dropdown"
                    [placeholder]="'SDS_EMPLOYEE_PICKER_PLACEHOLDER' | translate"
                    (onChange)="onEmployeeSelected(i, $event.value)">
                  </p-select>
                  <small class="sds-field-hint" [translate]="'SDS_EMPLOYEE_PICKER_HINT'"></small>
                </div>
                <div class="sds-field">
                  <label class="sds-field-label">
                    <span [translate]="'SDS_EMPLOYEE_FIRST_NAME'"></span>
                    <span class="sds-required-marker" aria-hidden="true">*</span>
                  </label>
                  <input type="text" pInputText formControlName="FirstName" />
                </div>
                <div class="sds-field">
                  <label class="sds-field-label">
                    <span [translate]="'SDS_EMPLOYEE_LAST_NAME'"></span>
                    <span class="sds-required-marker" aria-hidden="true">*</span>
                  </label>
                  <input type="text" pInputText formControlName="LastName" />
                </div>
                <div class="sds-field">
                  <label class="sds-field-label">Email</label>
                  <input type="email" pInputText formControlName="Email" />
                </div>
                <div class="sds-field">
                  <label class="sds-field-label" [translate]="'ADDRESS'"></label>
                  <input type="text" pInputText formControlName="Address" />
                </div>
                <div class="sds-field" *ngIf="requiresNationalIdentityNumber">
                  <label class="sds-field-label">
                    <span [translate]="'SDS_NIN_PLACEHOLDER'"></span>
                    <span class="sds-required-marker" aria-hidden="true">*</span>
                  </label>
                  <div class="sds-nin-edit">
                    <input
                      type="text"
                      pInputText
                      formControlName="NationalIdentityNumber"
                      autocomplete="off"
                      maxlength="11"
                      [placeholder]="exposure.value.NationalIdentityNumberMasked || ''"
                      [pTooltip]="'SDS_NIN_TOOLTIP_LEAVE_BLANK' | translate"
                      tooltipPosition="top"
                    />
                    <button
                      *ngIf="exposure.value.Id && exposure.value.NationalIdentityNumberMasked"
                      class="btn btn-link btn-sm"
                      type="button"
                      (click)="revealExposureNin(exposure.value.Id)"
                      [pTooltip]="'SDS_REVEAL_NIN_TOOLTIP' | translate"
                      tooltipPosition="top">
                      <i class="pi pi-eye"></i>
                    </button>
                  </div>
                  <small
                    *ngIf="exposure.get('NationalIdentityNumber')?.touched && exposure.get('NationalIdentityNumber')?.errors as ninErrors"
                    class="sds-field-error">
                    <span *ngIf="ninErrors['ninFormat']" [translate]="'SDS_NIN_VALIDATION_FORMAT'"></span>
                  </small>
                </div>
                <div class="sds-field" *ngIf="isAsbestos">
                  <label class="sds-field-label">
                    <span [translate]="'SDS_MEDICAL_EXAM_DATE'"></span>
                    <small class="sds-field-optional" [translate]="'OPTIONAL'"></small>
                  </label>
                  <p-datepicker
                    formControlName="MedicalExamDate"
                    dateFormat="dd/mm/yy"
                    [showIcon]="true"
                    appendTo="body"
                  ></p-datepicker>
                </div>
                <div class="sds-field" *ngIf="isAsbestos">
                  <label class="sds-field-label">
                    <span [translate]="'SDS_MEDICAL_EXAM_DOCTOR_NAME'"></span>
                    <small class="sds-field-optional" [translate]="'OPTIONAL'"></small>
                  </label>
                  <input type="text" pInputText formControlName="MedicalExamDoctorName" />
                </div>
                <div class="sds-exposure-date-row">
                  <div class="sds-field">
                    <label class="sds-field-label">
                      <span [translate]="'SDS_EXPOSURE_START_DATE'"></span>
                      <span class="sds-required-marker" aria-hidden="true">*</span>
                    </label>
                    <p-datepicker
                      formControlName="ExposureStartDate"
                      dateFormat="dd/mm/yy"
                      [showIcon]="true"
                      appendTo="body"
                    ></p-datepicker>
                  </div>
                  <div class="sds-field">
                    <label class="sds-field-label">
                      <span [translate]="'SDS_EXPOSURE_END_DATE'"></span>
                      <small class="sds-field-optional" [translate]="'OPTIONAL'"></small>
                    </label>
                    <p-datepicker
                      formControlName="ExposureEndDate"
                      dateFormat="dd/mm/yy"
                      [showIcon]="true"
                      appendTo="body"
                      [pTooltip]="'SDS_EXPOSURE_END_DATE_HINT' | translate"
                      tooltipPosition="top"
                    ></p-datepicker>
                  </div>
                </div>
                <button class="btn btn-icon sds-row-remove" type="button" (click)="removeEmployeeExposure(i)">
                  <i class="pi pi-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>
</div>

<!-- ═══════════ REVEAL NATIONAL IDENTITY NUMBER DIALOG ═══════════ -->
<p-dialog
  [header]="'SDS_REVEAL_NIN_DIALOG_TITLE' | translate"
  [(visible)]="revealDialogVisible"
  [style]="{ width: '520px', maxWidth: '94vw' }"
  [baseZIndex]="10000"
  [resizable]="false"
  [draggable]="false"
  [modal]="true"
  [closable]="!revealDialogLoading"
  styleClass="sds-reveal-dialog"
  (onHide)="closeRevealDialog()"
>
  <div class="sds-reveal-dialog-body">
    <div class="sds-reveal-warning">
      <i class="pi pi-shield"></i>
      <span [translate]="'SDS_REVEAL_NIN_DIALOG_BODY'"></span>
    </div>

    <div class="sds-reveal-target" *ngIf="revealDialogExposure">
      <strong>{{ revealDialogExposure.FirstName }} {{ revealDialogExposure.LastName }}</strong>
      <span *ngIf="revealDialogExposure.Email">{{ revealDialogExposure.Email }}</span>
      <span *ngIf="revealDialogExposure.NationalIdentityNumberMasked && !revealDialogRevealedNumber">
        {{ revealDialogExposure.NationalIdentityNumberMasked }}
      </span>
    </div>

    <ng-container *ngIf="!revealDialogRevealedNumber; else revealedNinResult">
      <label class="sds-field-label" for="revealReason" [translate]="'SDS_REVEAL_NIN_REASON_LABEL'"></label>
      <textarea
        id="revealReason"
        pTextarea
        rows="4"
        maxlength="500"
        [(ngModel)]="revealDialogReason"
        [ngModelOptions]="{ standalone: true }"
        [disabled]="revealDialogLoading"
        [placeholder]="'SDS_REVEAL_NIN_REASON_PLACEHOLDER' | translate"
        class="sds-reveal-reason"
      ></textarea>
      <small class="sds-reveal-help" [translate]="'SDS_REVEAL_NIN_REASON_HELP'"></small>
      <div class="sds-reveal-error" *ngIf="revealDialogError">
        <i class="pi pi-exclamation-triangle"></i>
        <span>{{ revealDialogError }}</span>
      </div>
    </ng-container>

    <ng-template #revealedNinResult>
      <div class="sds-reveal-result">
        <span class="sds-reveal-result-label" [translate]="'SDS_NIN_PLACEHOLDER'"></span>
        <strong>{{ revealDialogRevealedNumber }}</strong>
      </div>
    </ng-template>
  </div>

  <ng-template pTemplate="footer">
    <button
      *ngIf="!revealDialogRevealedNumber"
      type="button"
      class="btn btn-outline-secondary"
      (click)="closeRevealDialog()"
      [disabled]="revealDialogLoading"
    >
      <span [translate]="'CANCEL'"></span>
    </button>
    <button
      *ngIf="!revealDialogRevealedNumber"
      type="button"
      class="btn btn-primary"
      (click)="submitRevealNin()"
      [disabled]="revealDialogLoading"
    >
      <i class="pi" [ngClass]="revealDialogLoading ? 'pi-spin pi-spinner' : 'pi-eye'"></i>
      <span [translate]="'SDS_REVEAL_NIN'"></span>
    </button>
    <button
      *ngIf="revealDialogRevealedNumber"
      type="button"
      class="btn btn-primary"
      (click)="closeRevealDialog()"
    >
      <span [translate]="'CLOSE'"></span>
    </button>
  </ng-template>
</p-dialog>

<!-- ═══════════ PDF PREVIEW DIALOG ═══════════ -->
<p-dialog
  [header]="'SDS_PDF_PREVIEW_TITLE' | translate"
  [(visible)]="pdfPreviewVisible"
  [style]="{ width: '90vw', height: '90vh' }"
  [baseZIndex]="10000"
  [maximizable]="true"
  [resizable]="false"
  [draggable]="false"
  [modal]="true"
  (onHide)="closePdfPreview()"
  styleClass="sds-pdf-dialog"
>
  <div class="sds-pdf-dialog-toolbar">
    <div class="sds-pdf-dialog-hint">
      <i class="pi pi-info-circle"></i>
      <span>{{ record?.SdsFileName }}</span>
    </div>
    <div class="sds-pdf-dialog-actions">
      <button
        *ngIf="hasUploadedPdf"
        class="btn btn-outline-primary sds-pdf-download-btn"
        type="button"
        (click)="downloadPdf()"
        [pTooltip]="'SDS_DOWNLOAD_PDF' | translate"
        tooltipPosition="left"
      >
        <i class="pi pi-download"></i>
        <span [translate]="'SDS_DOWNLOAD_PDF'"></span>
      </button>
    </div>
  </div>

  <div class="sds-pdf-no-file" *ngIf="pdfPreviewLoading">
    <i class="pi pi-spin pi-spinner"></i>
    <span [translate]="'PROCESSING'"></span>
  </div>
  <div class="sds-pdf-iframe-wrapper" *ngIf="!pdfPreviewLoading && pdfPreviewUrl">
    <iframe
      [src]="pdfPreviewUrl"
      class="sds-pdf-iframe"
      frameborder="0"
    ></iframe>
  </div>
  <div class="sds-pdf-no-file" *ngIf="!pdfPreviewLoading && !pdfPreviewUrl">
    <i class="pi pi-exclamation-triangle"></i>
    <span [translate]="'SDS_PDF_NOT_AVAILABLE'"></span>
  </div>
</p-dialog>

<!-- ═══════════ ASSIGN REPLACEMENT (existing chemical) DIALOG ═══════════ -->
<p-dialog
  [header]="'SDS_REPLACEMENT_USE_EXISTING' | translate"
  [(visible)]="replaceDialogVisible"
  [style]="{ width: '520px', maxWidth: '94vw' }"
  [baseZIndex]="10000"
  [resizable]="false"
  [draggable]="false"
  [modal]="true"
  [closable]="!replaceSaving"
  (onHide)="closeReplaceDialog()"
>
  <div class="sds-reveal-dialog-body">
    <p [translate]="'SDS_REPLACEMENT_USE_EXISTING_HINT'"></p>

    <div class="sds-reveal-field">
      <label class="sds-field-label">
        {{ 'SDS_REPLACEMENT_SELECT_LABEL' | translate }}
        <span class="sds-required-marker" aria-hidden="true">*</span>
      </label>
      <p-autoComplete
        [(ngModel)]="selectedReplacementCandidate"
        [suggestions]="replaceCandidateSuggestions"
        (completeMethod)="searchReplacementCandidates($event)"
        [forceSelection]="true"
        [minLength]="1"
        [delay]="200"
        appendTo="body"
        optionLabel="Product"
        dataKey="DocumentId"
        [placeholder]="'SDS_REPLACEMENT_SEARCH_PLACEHOLDER' | translate"
        [disabled]="replaceSaving"
      >
        <ng-template let-candidate pTemplate="item">
          <div class="sds-replacement-candidate">
            <strong>{{ candidate.Product }}</strong>
            <small *ngIf="candidate.CASNumber"> · {{ candidate.CASNumber }}</small>
            <small *ngIf="candidate.Supplier"> · {{ candidate.Supplier }}</small>
          </div>
        </ng-template>
      </p-autoComplete>
    </div>

    <div class="sds-reveal-field">
      <label class="sds-field-label">
        {{ 'SDS_REPLACEMENT_REASON_LABEL' | translate }}
        <small class="sds-field-optional" [translate]="'OPTIONAL'"></small>
      </label>
      <textarea
        pTextarea
        rows="3"
        maxlength="1000"
        [(ngModel)]="replaceReason"
        [disabled]="replaceSaving"
        [placeholder]="'SDS_REPLACEMENT_REASON_PLACEHOLDER' | translate"
        (input)="replaceError = null"
      ></textarea>
    </div>

    <div class="sds-reveal-error" *ngIf="replaceError">
      <i class="pi pi-exclamation-triangle"></i>
      <span>{{ replaceError }}</span>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <button class="btn btn-outline-secondary" type="button" (click)="closeReplaceDialog()" [disabled]="replaceSaving">
      <span [translate]="'CANCEL'"></span>
    </button>
    <button
      class="btn btn-primary"
      type="button"
      (click)="confirmAssignReplacement()"
      [disabled]="replaceSaving || !selectedReplacementCandidate"
    >
      <i class="pi" [ngClass]="replaceSaving ? 'pi-spin pi-spinner' : 'pi-sync'"></i>
      <span [translate]="'SDS_REPLACEMENT_CONFIRM'"></span>
    </button>
  </ng-template>
</p-dialog>

<!-- ═══════════ ADD MANUFACTURER DIALOG ═══════════ -->
<p-dialog
  [header]="'SDS_NEW_SUPPLIER' | translate"
  [(visible)]="addSupplierVisible"
  [style]="{ width: '400px' }"
  [resizable]="false"
  [draggable]="false"
  [modal]="true"
>
  <div class="form-group">
    <label class="col-form-label" [translate]="'SDS_SUPPLIER_NAME'"></label>
    <input
      type="text"
      pInputText
      [(ngModel)]="newSupplierName"
      class="ex-full-width"
      [placeholder]="'SDS_SUPPLIER_NAME_PLACEHOLDER' | translate"
      (keyup.enter)="saveNewSupplier()"
    />
  </div>
  <ng-template pTemplate="footer">
    <button class="btn btn-primary" [disabled]="!newSupplierName.trim()" (click)="saveNewSupplier()">
      <span [translate]="'SAVE'"></span>
    </button>
  </ng-template>
</p-dialog>

<!-- ═══════════ TOASTER & LOADER ═══════════ -->

<p-progressBar
  mode="indeterminate"
  [style]="{ height: '6px' }"
  *ngIf="loading"
></p-progressBar>

<kuba-busy [busy]="loading" message="PROCESSING"></kuba-busy>

